import { useEffect, useMemo, useState } from "react";

import { listInsurers } from "../api/patients";
import { Select } from "../ui/select";
import type { SelectOption } from "../ui/select";

/**
 * The insurer, as a dropdown.
 *
 * ── It used to be a `datalist`, and why that changed ────────────────────────
 *
 * The argument for a combo text box was real: a `<select>` cannot hold a value
 * that is not already an option, and **the first patient at a new clinic is
 * exactly that case** — nothing to choose from until somebody types one. So
 * this was `<input list>`: a dropdown that still accepted a new name.
 *
 * In practice it read as a text box, which is what it was, and the client asked
 * for a dropdown behaving like every other dropdown — *"for now fill it with
 * Self-Pay and insurance"*.
 *
 * ── The day-one problem is solved by seeding the list, not by typing ────────
 *
 * `BASE` gives every clinic two answers before it has any history, so the field
 * is usable on the first patient without anybody inventing a name. Beyond that
 * the list is still **the clinic's own answers played back** — there is no
 * shipped list of insurance companies, because that would be invented clinic
 * data and wrong outside whichever country it was written for.
 *
 * What it keeps fixing is spelling: `Bupa`, `BUPA` and `Bupa Global`
 * accumulating as three insurers because three people typed them.
 *
 * ── Nothing already recorded is lost ────────────────────────────────────────
 *
 * A patient whose insurer was typed before this change keeps it: the current
 * value is added to the list whenever it is not already there. Without that,
 * opening a file to change a date of birth would silently blank the insurer.
 *
 * ── A real `insurers` table would still be better ───────────────────────────
 *
 * One canonical row per insurer, an admin screen, and a merge for the names
 * already recorded. That is a migration after Phase 0, so it stays the client's
 * decision — raised in OPEN-QUESTIONS.
 *
 * **A failed read is silent.** The list is a convenience; an unreachable server
 * must not stop somebody recording a patient's insurer, and the two base
 * answers are always there.
 */

/**
 * The two the client asked for. **Not clinic data**: they are the two ways
 * anybody pays anywhere, not a named company, so they are safe to ship — see
 * CLAUDE.md, *Data is configured, not coded*, which forbids the named insurer
 * and not the payment model.
 */
const BASE = ["Self-pay", "Insurance"];

export function InsurerField({
  value,
  onChange,
  testId,
  className,
}: {
  value: string;
  onChange: (next: string) => void;
  testId: string;
  // Optional, because a CSS-module class is typed as possibly-undefined under
  // this project's settings and the caller passes theirs straight through.
  className?: string;
}): React.JSX.Element {
  const [known, setKnown] = useState<readonly string[]>([]);

  useEffect(() => {
    const controller = new AbortController();

    listInsurers(controller.signal)
      .then(setKnown)
      .catch(() => {
        /* Silent — see above. */
      });

    return () => controller.abort();
  }, []);

  const options = useMemo<SelectOption[]>(() => {
    const seen = new Set<string>();
    const all: string[] = [];

    for (const name of [...BASE, ...known, ...(value === "" ? [] : [value])]) {
      // Case-insensitively de-duplicated, because "Self-Pay" coming back from
      // the clinic's own history must not sit beside the "Self-pay" above it.
      const key = name.trim().toLowerCase();
      if (key === "" || seen.has(key)) continue;
      seen.add(key);
      all.push(name.trim());
    }

    return all.map((name) => ({ value: name, label: name }));
  }, [known, value]);

  return (
    <div className={className}>
      <Select
        id={testId}
        label="Insurer"
        value={value}
        options={options}
        onChange={onChange}
        testId={testId}
        emptyLabel="Not recorded"
      />
    </div>
  );
}
